import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  ArrowLeft, ArrowRight, Loader2, Download, Lightbulb, Rocket,
  ExternalLink, Copy, Check, Zap, Wrench, TrendingUp, Clock
} from 'lucide-react';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IDEA_TYPES = [
  { value: 'business', label: 'Business / Startup' },
  { value: 'content', label: 'Content / Media' },
  { value: 'service', label: 'Consulting / Service' },
  { value: 'product', label: 'Product / App' },
  { value: 'community', label: 'Community / Platform' },
  { value: 'other', label: 'Other' },
];

export default function IdeaLab() {
  const navigate = useNavigate();
  const [idea, setIdea] = useState('');
  const [ideaType, setIdeaType] = useState('business');
  const [result, setResult] = useState(null);
  const [pastIdeas, setPastIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState(null);
  const pdfRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('pivot_user') || '{}');

  useEffect(() => { loadPastIdeas(); }, []);

  const loadPastIdeas = async () => {
    try {
      const res = await axios.get(`${API}/idea-lab/${user.user_id}`);
      setPastIdeas(res.data);
    } catch (e) { /* none */ }
  };

  const handleAnalyze = async () => {
    if (!idea.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/idea-lab/analyze`, {
        user_id: user.user_id, idea, idea_type: ideaType
      });
      if (res.data.status === 'success') {
        setResult(res.data.result);
        toast.success('Idea analysis complete!');
        loadPastIdeas();
      } else {
        toast.error(res.data.message || 'Analysis failed');
      }
    } catch (e) {
      toast.error('Something went wrong');
    } finally { setLoading(false); }
  };

  const copyPrompt = (prompt, idx) => {
    navigator.clipboard.writeText(prompt);
    setCopiedPrompt(idx);
    toast.success('Prompt copied!');
    setTimeout(() => setCopiedPrompt(null), 2000);
  };

  const downloadPDF = async () => {
    if (!pdfRef.current) return;
    toast.info('Generating PDF...');
    try {
      const canvas = await html2canvas(pdfRef.current, { scale: 2, backgroundColor: '#f9fafb', useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      let heightLeft = pdfHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();
      while (heightLeft > 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }
      pdf.save(`Pivot-Idea-Lab-${result?.idea_title?.replace(/\s+/g, '-') || 'Analysis'}.pdf`);
      toast.success('PDF downloaded!');
    } catch (e) {
      toast.error('PDF generation failed');
    }
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-cormorant font-bold text-white mb-1" data-testid="idea-lab-title">Power Lab</h1>
            <p className="text-[#A39BB0] font-inter text-sm">Describe your idea. We'll tell you exactly how to build it.</p>
          </div>
          {result && (
            <button onClick={downloadPDF} className="flex items-center space-x-2 bg-white/10 hover:bg-white/15 text-white font-inter text-sm px-4 py-2 rounded-full transition-all border border-white/10" data-testid="download-pdf-button">
              <Download className="w-4 h-4" /><span>Download PDF</span>
            </button>
          )}
        </div>

        {!result ? (
          <>
            {/* Input Section */}
            <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-8 mb-6" data-testid="idea-input-section">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-500 rounded-xl flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-cormorant font-bold text-white">What's Your Idea?</h2>
                  <p className="text-[#A39BB0] text-xs font-inter">Be as detailed as possible</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-purple-300 text-sm font-inter font-medium mb-2">Idea Type</label>
                  <div className="flex flex-wrap gap-2">
                    {IDEA_TYPES.map(t => (
                      <button key={t.value} onClick={() => setIdeaType(t.value)}
                        className={`px-4 py-2 rounded-full text-sm font-inter font-medium transition-all ${
                          ideaType === t.value ? 'bg-purple-600 text-white' : 'bg-white/5 text-[#A39BB0] border border-white/10 hover:bg-white/10 hover:text-white'
                        }`} data-testid={`idea-type-${t.value}`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-purple-300 text-sm font-inter font-medium mb-2">Describe Your Idea</label>
                  <textarea value={idea} onChange={(e) => setIdea(e.target.value)}
                    className="w-full bg-white/[0.05] border border-white/[0.08] rounded-xl px-4 py-4 text-white focus:border-purple-500/50 outline-none min-h-[160px] font-inter text-sm resize-none leading-relaxed placeholder:text-[#A39BB0]/40"
                    placeholder="Example: I want to build an AI-powered resume coaching service for professionals pivoting careers..."
                    data-testid="idea-input" />
                </div>
                <button onClick={handleAnalyze} disabled={loading || !idea.trim()}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold py-4 rounded-xl shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] transition-all disabled:opacity-50"
                  data-testid="analyze-idea-button">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Analyzing Your Idea...</> : <>Analyze & Build Plan <Rocket className="w-4 h-4 inline ml-1" /></>}
                </button>
              </div>
            </div>

            {/* Past Ideas */}
            {pastIdeas.length > 0 && (
              <div>
                <h3 className="text-lg font-cormorant font-bold text-white mb-3">Previous Ideas</h3>
                <div className="space-y-2">
                  {pastIdeas.map((item, i) => (
                    <button key={i} onClick={() => setResult(item.result)}
                      className="w-full bg-white/[0.05] rounded-xl border border-white/[0.08] p-4 text-left hover:border-white/15 transition-all"
                      data-testid={`past-idea-${i}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-white font-inter font-medium text-sm">{item.result?.idea_title}</h4>
                          <p className="text-[#A39BB0] text-xs font-inter mt-0.5">{item.result?.idea_summary}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-[#A39BB0]/60 text-xs font-inter">{new Date(item.created_at).toLocaleDateString()}</span>
                          <ArrowRight className="w-4 h-4 text-[#A39BB0]" />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div ref={pdfRef} className="space-y-6 pb-12 bg-white rounded-2xl p-6" style={{ color: '#111827' }}>
            {/* Header */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8" data-testid="idea-result-header">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-3xl font-cormorant font-bold text-gray-900">{result.idea_title}</h2>
                  <p className="text-gray-500 font-inter text-sm mt-1">{result.idea_summary}</p>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <div className="text-3xl font-cormorant font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">{result.viability_score}/10</div>
                  <div className="text-gray-400 text-xs font-inter">Viability</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                  <h4 className="text-emerald-700 text-xs font-inter font-bold uppercase mb-2">Strengths</h4>
                  {result.strengths?.map((s, i) => <p key={i} className="text-gray-700 text-sm font-inter">{s}</p>)}
                </div>
                <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
                  <h4 className="text-amber-700 text-xs font-inter font-bold uppercase mb-2">Risks</h4>
                  {result.risks?.map((r, i) => <p key={i} className="text-gray-700 text-sm font-inter">{r}</p>)}
                </div>
              </div>
            </div>

            {/* Phase 1: Flush Out */}
            <PhaseCard phase={result.phase_1_flush_out} num="01" color="purple" icon={Lightbulb} copyPrompt={copyPrompt} copiedPrompt={copiedPrompt} />

            {/* Phase 2: Build */}
            <PhaseCard phase={result.phase_2_build} num="02" color="pink" icon={Wrench} copyPrompt={copyPrompt} copiedPrompt={copiedPrompt} />

            {/* Phase 3: Launch */}
            <PhaseCard phase={result.phase_3_launch} num="03" color="emerald" icon={TrendingUp} copyPrompt={copyPrompt} copiedPrompt={copiedPrompt} />

            {/* Next Step */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-2xl p-8 text-center">
              <Zap className="w-8 h-8 text-white/80 mx-auto mb-2" />
              <h3 className="text-white font-cormorant font-bold text-2xl mb-2">Your Next Step</h3>
              <p className="text-white/90 font-inter leading-relaxed">{result.next_step}</p>
            </div>

            {/* Back to Input */}
            <div className="flex items-center justify-center space-x-3">
              <button onClick={() => { setResult(null); setIdea(''); }}
                className="flex items-center space-x-2 bg-white border border-gray-200 text-gray-700 font-inter text-sm px-5 py-2.5 rounded-full hover:border-purple-300 transition-all"
                data-testid="new-idea-button">
                <Lightbulb className="w-4 h-4" /><span>New Idea</span>
              </button>
              <button onClick={() => navigate('/profile-analyzer')}
                className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold text-sm px-5 py-2.5 rounded-full hover:shadow-lg hover:shadow-purple-200 transition-all"
                data-testid="go-to-analyzer">
                <span>View Profile Analysis</span><ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function PhaseCard({ phase, num, color, icon: Icon, copyPrompt, copiedPrompt }) {
  if (!phase) return null;
  return (
    <div className={`bg-white rounded-2xl border border-${color}-100 shadow-sm overflow-hidden`} data-testid={`phase-${num}`}>
      <div className={`bg-${color}-50 px-6 py-4 flex items-center space-x-3 border-b border-${color}-100`}>
        <div className={`w-10 h-10 bg-${color}-600 rounded-xl flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className={`text-${color}-700 text-xs font-inter font-bold uppercase tracking-wider`}>Phase {num}</div>
          <h3 className="text-gray-900 font-cormorant font-bold text-xl">{phase.title}</h3>
        </div>
      </div>
      <div className="p-6">
        <p className="text-gray-500 font-inter text-sm mb-5">{phase.description}</p>
        <div className="space-y-4">
          {phase.tools?.map((tool, i) => (
            <div key={i} className={`bg-gray-50 rounded-xl p-5 border border-gray-100`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-gray-900 font-inter font-bold text-sm">{tool.name}</h4>
                {tool.url && (
                  <a href={tool.url} target="_blank" rel="noreferrer" className="flex items-center space-x-1 text-purple-600 text-xs font-inter hover:underline">
                    <span>Open</span><ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <p className="text-gray-600 font-inter text-sm mb-3">{tool.purpose}</p>
              {tool.prompt_to_use && (
                <div className="bg-white border border-gray-200 rounded-lg p-3 relative">
                  <p className="text-gray-400 text-[10px] font-inter uppercase tracking-wider mb-1">Copy this prompt:</p>
                  <p className="text-gray-700 font-inter text-xs leading-relaxed pr-8">{tool.prompt_to_use}</p>
                  <button onClick={() => copyPrompt(tool.prompt_to_use, `${num}-${i}`)}
                    className="absolute top-3 right-3 text-gray-400 hover:text-purple-600 transition-colors"
                    data-testid={`copy-prompt-${num}-${i}`}>
                    {copiedPrompt === `${num}-${i}` ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              )}
              {tool.instructions && (
                <div className="mt-2">
                  <p className="text-gray-400 text-[10px] font-inter uppercase tracking-wider mb-1">Instructions:</p>
                  <p className="text-gray-700 font-inter text-xs leading-relaxed">{tool.instructions}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
